/**
 * Timezone-aware time utilities for time entry management.
 * Times are stored in UTC but displayed in the user's local timezone.
 */

/**
 * Get the user's timezone, defaulting to US Central Time.
 */
export function getUserTimezone(): string {
  try {
    const tz = Intl.DateTimeFormat().resolvedOptions().timeZone;
    return tz || 'America/Chicago';
  } catch {
    return 'America/Chicago';
  }
}

/**
 * Get a short timezone label for display (e.g., "CST", "EST").
 */
export function getTimezoneLabel(tz: string): string {
  try {
    const parts = new Intl.DateTimeFormat('en-US', {
      timeZone: tz,
      timeZoneName: 'short',
    }).formatToParts(new Date());
    return parts.find((p) => p.type === 'timeZoneName')?.value || tz;
  } catch {
    return tz;
  }
}

/**
 * Extract time (HH:MM) from a UTC ISO datetime string, converted to the given timezone.
 * Returns empty string if the input doesn't contain time information.
 */
export function utcToLocalTime(utcIsoString: string | undefined, tz: string): string {
  if (!utcIsoString || !utcIsoString.includes('T')) return '';
  const date = new Date(utcIsoString);
  if (isNaN(date.getTime())) return '';

  const parts = new Intl.DateTimeFormat('en-GB', {
    timeZone: tz,
    hour: '2-digit',
    minute: '2-digit',
    hour12: false,
  }).formatToParts(date);

  const hour = parts.find((p) => p.type === 'hour')?.value || '00';
  const minute = parts.find((p) => p.type === 'minute')?.value || '00';
  return `${hour}:${minute}`;
}

/**
 * Convert a local date + time in a specific timezone to a UTC ISO string.
 * Returns empty string if either input is missing or invalid.
 */
export function localToUtcIso(dateStr: string, timeStr: string, tz: string): string {
  if (!dateStr || !timeStr) return '';

  const [year, month, day] = dateStr.split('-').map(Number);
  const [hours, minutes] = timeStr.split(':').map(Number);

  if ([year, month, day, hours, minutes].some(isNaN)) return '';

  // Create a date at the "desired" local time, pretending it's UTC
  const fakeUtc = new Date(Date.UTC(year, month - 1, day, hours, minutes, 0));

  // Determine the timezone offset by formatting fakeUtc in the target timezone
  const inTz = new Intl.DateTimeFormat('en-US', {
    timeZone: tz,
    hour: 'numeric',
    minute: 'numeric',
    hour12: false,
    day: 'numeric',
    month: 'numeric',
    year: 'numeric',
  }).formatToParts(fakeUtc);

  const getValue = (type: string) =>
    parseInt(inTz.find((p) => p.type === type)?.value || '0', 10);

  let tzHour = getValue('hour');
  if (tzHour === 24) tzHour = 0; // midnight edge case
  const tzMin = getValue('minute');
  const tzDay = getValue('day');

  // Offset = (timezone local time) - (UTC time we sent), in minutes
  let offsetMin = tzHour * 60 + tzMin - (hours * 60 + minutes);
  if (tzDay > day) offsetMin += 24 * 60;
  else if (tzDay < day) offsetMin -= 24 * 60;

  // UTC = desired local time − offset
  const resultMs = fakeUtc.getTime() - offsetMin * 60_000;
  // Return in YYYY-MM-DDTHH:mm:ssZ format (no milliseconds)
  return new Date(resultMs).toISOString().replace(/\.\d{3}Z$/, 'Z');
}

/**
 * Calculate the difference in hours between two HH:MM time strings.
 * If end < start, assumes crossing midnight.
 */
export function calculateHoursDiff(startTime: string, endTime: string): number {
  if (!startTime || !endTime) return 0;

  const [startH, startM] = startTime.split(':').map(Number);
  const [endH, endM] = endTime.split(':').map(Number);

  if ([startH, startM, endH, endM].some(isNaN)) return 0;

  let diffMinutes = endH * 60 + endM - (startH * 60 + startM);
  if (diffMinutes < 0) diffMinutes += 24 * 60; // crossed midnight

  return diffMinutes / 60;
}

/**
 * Extract the date portion (YYYY-MM-DD) from a datetime or date string.
 */
export function extractDatePortion(dateStr: string | undefined): string {
  if (!dateStr) return '';
  return dateStr.split('T')[0];
}
